import type { ANNOTATION_SHAPE_STYLING_TYPE } from '../../constants';
import { ANNOTATION_STYLING_DETAILS } from '../../constants';
import { type AnnotationType, ANNOTATION_TYPE } from '../../constants';
import type { MultiSelectBoxChange } from '../../providers/stage-provider';
import { type MousePosT } from '../../providers/stage-provider';
import type { AnnotationDataT, AnnotationT, CuboidAnnotationT, CuboidDataT, EllipseDataT, PointAnnotationT, PolygonAnnotationT, PolygonDataT, PolylineAnnotationT, PolylineDataT, RectDataT, SkeletonAnnotationT, SkeletonDataT } from '../../types/annotations.type';
import { getTransformedRectDataPointsOnVertexMove } from './multi-select/utils';
import { convertDeltaToMousePosition, getRectangleVertices } from './rectangle/utils';
import { VERTEX_RADIUS } from './shape/vertex';
import { calculateBoundingBox } from './utils/scaling-utils';
export const BOX_BUFFER = 20;
type RectCoordinates = [[number, number], [number, number], [number, number], [number, number]];
export const getRectAttributesFromCoordinates = coordinates => {
  const [topLeft, bottomRight] = coordinates;
  const x = topLeft[0];
  const y = topLeft[1];
  const width = bottomRight[0] - x;
  const height = bottomRight[1] - y;
  return {
    x,
    y,
    width,
    height
  };
};
export const getRectangleCoordinates = (rectAttributes): RectCoordinates => {
  const {
    x,
    y,
    width,
    height
  } = rectAttributes;
  const topLeft: [number, number] = [x, y];
  const topRight: [number, number] = [x + width, y];
  const bottomRight: [number, number] = [x + width, y + height];
  const bottomLeft: [number, number] = [x, y + height];
  return [topLeft, topRight, bottomRight, bottomLeft];
};
const dimensionHandlers = {
  rect: (data: RectDataT): RectDataT => data,
  ellipse: (data: EllipseDataT): RectDataT => ({
    height: data.radiusY * 2,
    width: data.radiusX * 2,
    x: data.centerX - data.radiusX,
    y: data.centerY - data.radiusY
  }),
  points: (data: PolylineDataT | CuboidDataT | PolygonDataT): RectDataT => {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const point of data.points) {
      const [x, y] = point;
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
    const width = maxX - minX;
    const height = maxY - minY;
    return {
      x: minX,
      y: minY,
      width,
      height,
      rotationAngle: 0
    };
  },
  point: (data): RectDataT => {
    return {
      x: data.x,
      y: data.y,
      width: 0,
      height: 0,
      rotationAngle: 0
    };
  },
  skeleton: (data: SkeletonDataT): RectDataT | null => {
    if (!data.nodes.length) return null;
    const points = data.nodes.map(node => node.point);
    const bbox = calculateBoundingBox(points, 0);
    return {
      x: bbox.centerX - bbox.width / 2,
      y: bbox.centerY - bbox.height / 2,
      width: bbox.width,
      height: bbox.height,
      rotationAngle: 0
    };
  }
};
export const getDimensionsOfAnyShape = (data: AnnotationDataT, type: AnnotationType): RectDataT | null => {
  let handler: (AnnotationDataT) => RectDataT | null = () => null;
  if (type === ANNOTATION_TYPE.RECTANGLE) {
    handler = dimensionHandlers.rect;
  }
  const TYPES_WITH_POINTS: AnnotationType[] = [ANNOTATION_TYPE.POLYGON, ANNOTATION_TYPE.POLYLINE, ANNOTATION_TYPE.CUBOID];
  if (TYPES_WITH_POINTS.indexOf(type) >= 0) {
    handler = dimensionHandlers.points;
  }
  if (type === ANNOTATION_TYPE.KEY_POINT) {
    handler = dimensionHandlers.point;
  }
  if (type === ANNOTATION_TYPE.ELLIPSE) {
    handler = dimensionHandlers.ellipse;
  }
  if (type === ANNOTATION_TYPE.SKELETON) {
    handler = dimensionHandlers.skeleton;
  }
  return handler(data);
};
export const getGroupingBoxDimensions = (annotations: Array<AnnotationT>): RectDataT => {
  const reduced = annotations.reduce<RectDataT>((a, b, i) => {
    const currentDimensions = getDimensionsOfAnyShape(b.data, b.annotationType);
    if (!currentDimensions) {
      return a;
    }
    const {
      height,
      width,
      x,
      y
    } = a;
    const {
      height: currHeight,
      width: currWidth,
      x: currX,
      y: currY
    } = currentDimensions;
    if (i === 0) {
      return {
        height: currHeight,
        width: currWidth,
        x: currX,
        y: currY,
        rotationAngle: 0
      };
    }
    const maxX = Math.max(x + width, currX + currWidth);
    const minX = Math.min(x, currX);
    const maxY = Math.max(y + height, currY + currHeight);
    const minY = Math.min(y, currY);
    return {
      height: maxY - minY,
      width: maxX - minX,
      x: Math.min(x, currX),
      y: Math.min(y, currY),
      rotationAngle: 0
    };
  }, {
    height: 0,
    width: 0,
    rotationAngle: 0,
    x: 0,
    y: 0
  });
  return {
    ...reduced,
    height: reduced.height + BOX_BUFFER,
    width: reduced.width + BOX_BUFFER,
    x: reduced.x - BOX_BUFFER / 2,
    y: reduced.y - BOX_BUFFER / 2
  };
};
export const getAdjustedPointAnnotation = ({
  pointAnnotation,
  maxX,
  minX,
  maxY,
  minY
}: {
  pointAnnotation: PointAnnotationT;
  maxX: number;
  minX: number;
  maxY: number;
  minY: number;
}): PointAnnotationT => {
  const adjustedAnnotation: PointAnnotationT = {
    ...pointAnnotation
  };
  adjustedAnnotation.data.x = Math.min(Math.max(pointAnnotation.data.x, minX), maxX);
  adjustedAnnotation.data.y = Math.min(Math.max(pointAnnotation.data.y, minY), maxY);
  return adjustedAnnotation;
};
export const getAdjustedPointsAnnotation = ({
  pointsAnnotation,
  maxX,
  minX,
  maxY,
  minY
}: {
  pointsAnnotation: PolygonAnnotationT | PolylineAnnotationT;
  maxX: number;
  minX: number;
  maxY: number;
  minY: number;
}): PolygonAnnotationT | PolylineAnnotationT => {
  const adjustedAnnotation: PolygonAnnotationT | PolylineAnnotationT = {
    ...pointsAnnotation
  };
  adjustedAnnotation.data.points = adjustedAnnotation.data.points.map(([x, y]) => [Math.min(Math.max(x, minX), maxX), Math.min(Math.max(y, minY), maxY)]);
  return adjustedAnnotation;
};
export const getAdjustedCuboidAnnotation = ({
  cuboidAnnotation,
  multiSelectBoxChange,
  multiSelectBox
}: {
  cuboidAnnotation: CuboidAnnotationT;
  multiSelectBox: RectDataT | null | undefined;
  multiSelectBoxChange: MultiSelectBoxChange | null | undefined;
}): CuboidAnnotationT => {
  if (!multiSelectBox || !multiSelectBoxChange || typeof multiSelectBoxChange.selectedMultiBoxVertexType !== 'string') {
    return cuboidAnnotation;
  }
  const adjustedAnnotation: CuboidAnnotationT = {
    ...cuboidAnnotation
  };
  const rectangle1Points = adjustedAnnotation.data.points.slice(0, 4);
  const rectangle2Points = adjustedAnnotation.data.points.slice(4);
  const rectangle1 = getRectAttributesFromCoordinates([rectangle1Points[0], rectangle1Points[2]]);
  const rectangle2 = getRectAttributesFromCoordinates([rectangle2Points[0], rectangle2Points[2]]);
  const vertices1 = getRectangleVertices(rectangle1);
  const vertices2 = getRectangleVertices(rectangle2);
  const selectedVertex1 = vertices1.find(v => v.type === multiSelectBoxChange.selectedMultiBoxVertexType);
  const selectedVertex2 = vertices2.find(v => v.type === multiSelectBoxChange.selectedMultiBoxVertexType);
  if (!selectedVertex1 || !selectedVertex2) {
    return adjustedAnnotation;
  }
  const [x1, y1] = selectedVertex1.point;
  const convertedMousePosition1: [number, number] = convertDeltaToMousePosition({
    x: x1,
    y: y1,
    deltaWidth: multiSelectBoxChange.changeWidth,
    deltaHeight: multiSelectBoxChange.changeHeight,
    vertexType: multiSelectBoxChange.selectedMultiBoxVertexType
  });
  const [x2, y2] = selectedVertex2.point;
  const convertedMousePosition2: [number, number] = convertDeltaToMousePosition({
    x: x2,
    y: y2,
    deltaWidth: multiSelectBoxChange.changeWidth,
    deltaHeight: multiSelectBoxChange.changeHeight,
    vertexType: multiSelectBoxChange.selectedMultiBoxVertexType
  });
  const rectData1 = getTransformedRectDataPointsOnVertexMove(rectangle1, convertedMousePosition1, multiSelectBoxChange.selectedMultiBoxVertexType, multiSelectBox);
  const rectData2 = getTransformedRectDataPointsOnVertexMove(rectangle2, convertedMousePosition2, multiSelectBoxChange.selectedMultiBoxVertexType, multiSelectBox);
  const rectPoints1: RectCoordinates = getRectangleCoordinates(rectData1);
  const rectPoints2: RectCoordinates = getRectangleCoordinates(rectData2);
  adjustedAnnotation.data.points = [...rectPoints1, ...rectPoints2];
  return adjustedAnnotation;
};
export const ensureAnnotationWithinMultiSelectBox = (newAnnotation: AnnotationT, multiSelectBox: RectDataT, multiSelectBoxChange?: MultiSelectBoxChange | null | undefined) => {
  const {
    x,
    y,
    width: widthLimit,
    height: heightLimit
  } = multiSelectBox;
  const minX = x + BOX_BUFFER / 2;
  const minY = y + BOX_BUFFER / 2;
  const maxX = x + widthLimit - BOX_BUFFER / 2;
  const maxY = y + heightLimit - BOX_BUFFER / 2;
  if (newAnnotation.annotationType === ANNOTATION_TYPE.KEY_POINT) {
    return getAdjustedPointAnnotation({
      pointAnnotation: newAnnotation,
      maxX,
      minX,
      maxY,
      minY
    });
  }
  if (newAnnotation.annotationType === ANNOTATION_TYPE.POLYGON || newAnnotation.annotationType === ANNOTATION_TYPE.POLYLINE) {
    return getAdjustedPointsAnnotation({
      pointsAnnotation: newAnnotation,
      maxX,
      minX,
      maxY,
      minY
    });
  }
  if (newAnnotation.annotationType === ANNOTATION_TYPE.CUBOID) {
    return getAdjustedCuboidAnnotation({
      cuboidAnnotation: newAnnotation,
      multiSelectBoxChange,
      multiSelectBox
    });
  }
  return newAnnotation;
};
export const checkIfCommonPoint = (point: MousePosT, point0: MousePosT, scale: number) => {
  const a = point0[0] - point[0];
  const b = point0[1] - point[1];
  const distance = Math.sqrt(a * a + b * b);
  if (distance < 2 * VERTEX_RADIUS / scale) return true;
  return false;
};
export const getPointDistance = (pointA: MousePosT, pointB: MousePosT) => {
  return Math.sqrt((pointB[0] - pointA[0]) ** 2 + (pointB[1] - pointA[1]) ** 2);
};
export const getTranslatedPolyPoints = (deltaX: number, deltaY: number, points: Array<[number, number]>): Array<[number, number]> => {
  return points.map(([x, y]) => [x + deltaX, y + deltaY]);
};
export const getTransformedPolyPointsOnVertexMove = (mousePos: MousePosT, points: Array<[number, number]>, selectedVertexIndex: number): Array<[number, number]> => {
  return points.map((point, index) => index === selectedVertexIndex ? mousePos : point);
};
export const getTranslatedSkeletonNodes = (deltaX: number, deltaY: number, nodes: SkeletonAnnotationT['data']['nodes'], stageWidth: number, stageHeight: number): SkeletonAnnotationT['data']['nodes'] => {
  const translatedNodes = nodes.map(node => ({
    ...node,
    point: ([node.point[0] + deltaX, node.point[1] + deltaY] as [number, number])
  }));

  // Constrain all nodes to stage bounds if dimensions provided
  if (stageWidth !== undefined && stageHeight !== undefined) {
    // Find the bounding box of all nodes
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const node of translatedNodes) {
      minX = Math.min(minX, node.point[0]);
      minY = Math.min(minY, node.point[1]);
      maxX = Math.max(maxX, node.point[0]);
      maxY = Math.max(maxY, node.point[1]);
    }

    // Calculate offset needed to bring nodes back into bounds
    let offsetX = 0;
    let offsetY = 0;
    if (minX < 0) {
      offsetX = -minX;
    } else if (maxX > stageWidth) {
      offsetX = stageWidth - maxX;
    }
    if (minY < 0) {
      offsetY = -minY;
    } else if (maxY > stageHeight) {
      offsetY = stageHeight - maxY;
    }

    // Apply offset to all nodes if needed
    if (offsetX !== 0 || offsetY !== 0) {
      return translatedNodes.map(node => ({
        ...node,
        point: ([node.point[0] + offsetX, node.point[1] + offsetY] as [number, number])
      }));
    }
  }
  return translatedNodes;
};
export const getTransformedSkeletonNodesOnVertexMove = (mousePos: MousePosT, nodes: SkeletonAnnotationT['data']['nodes'], selectedVertexIndex: number): SkeletonAnnotationT['data']['nodes'] => {
  return nodes.map((node, index) => index === selectedVertexIndex ? {
    ...node,
    point: mousePos
  } : node);
};
export const getStylesForStylingType = (stylingType?: (typeof ANNOTATION_SHAPE_STYLING_TYPE)[keyof typeof ANNOTATION_SHAPE_STYLING_TYPE]) => {
  if (stylingType === undefined) {
    return {};
  }
  return {
    strokeDasharray: ANNOTATION_STYLING_DETAILS[stylingType].strokeDasharray
  };
};
export const findPointExistOnTheLine = (point: [number, number], line: {
  start: [number, number];
  end: [number, number];
}, radius: number) => {
  const smallerX = Math.min(line.start[0], line.end[0]);
  const largerX = Math.max(line.start[0], line.end[0]);
  const smallerY = Math.min(line.start[1], line.end[1]);
  const largerY = Math.max(line.start[1], line.end[1]);
  if (point[0] <= smallerX - radius || point[0] >= largerX + radius) {
    return false;
  }
  if (point[1] <= smallerY - radius || point[1] >= largerY + radius) {
    return false;
  }
  // equation of line -> Ax + By + C = 0
  const a = line.start[1] - line.end[1];
  const b = line.end[0] - line.start[0];
  const c = line.start[0] * line.end[1] - line.end[0] * line.start[1];
  const distance = Math.abs((a * point[0] + b * point[1] + c) / Math.sqrt(Math.pow(a, 2) + Math.pow(b, 2)));
  return distance < radius;
};
export const clampToStageBounds = (value: number, min = 0, max: number): number => {
  return Math.max(min, Math.min(value, max));
};
export const clampPointToStageBounds = (point: [number, number], stageWidth: number, stageHeight: number): [number, number] => {
  return [clampToStageBounds(point[0], 0, stageWidth), clampToStageBounds(point[1], 0, stageHeight)];
};
export const clampPointsToStageBounds = (points: Array<[number, number]>, stageWidth: number, stageHeight: number): Array<[number, number]> => {
  return points.map(point => clampPointToStageBounds(point, stageWidth, stageHeight));
};
export const constrainPointsToStageBounds = (points: Array<[number, number]>, stageWidth: number, stageHeight: number): {
  points: Array<[number, number]>;
  outOfBounds: boolean;
} => {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [x, y] of points) {
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }
  const outOfBounds = minX < 0 || minY < 0 || maxX > stageWidth || maxY > stageHeight;
  if (!outOfBounds) {
    return {
      points,
      outOfBounds: false
    };
  }
  let offsetX = 0;
  let offsetY = 0;
  if (minX < 0) {
    offsetX = -minX;
  } else if (maxX > stageWidth) {
    offsetX = stageWidth - maxX;
  }
  if (minY < 0) {
    offsetY = -minY;
  } else if (maxY > stageHeight) {
    offsetY = stageHeight - maxY;
  }
  const constrainedPoints = points.map(([x, y]): [number, number] => [x + offsetX, y + offsetY]);
  return {
    points: constrainedPoints,
    outOfBounds: true
  };
};